// src/admin/screens/collections/EscalationBar.tsx
// Tells the agent what the next play is for this case, based on days
// overdue. The steps mirror the Delinquency Escalation ladder in
// CLAUDE.md (Day 1 SMS / Day 7 call / Day 14 delinquent / Day 30 demand
// notice / Day 60 bureau / Day 90 default recommendation) so what the
// agent sees here is the same playbook product + compliance agreed on.
import type { CSSProperties } from 'react';
import Card from '@admin/components/Card';
import { formatCatDate } from '@admin/utils/dates';

interface Props {
  daysOverdue: number;
  lastContactAt: string | null;
}

interface Step {
  label: string;
  prompt: string;
  tone: 'info' | 'warn' | 'danger';
}

function nextStep(days: number): Step {
  if (days < 1) return { label: 'Not yet overdue', prompt: 'Monitor. Automated SMS will schedule on due date.', tone: 'info' };
  if (days < 3) return { label: 'Day 1–2', prompt: 'Automated SMS reminder is going out. No agent action needed yet.', tone: 'info' };
  if (days < 7) return { label: 'Day 3–6', prompt: 'Second SMS + in-app notification is queued. Monitor response.', tone: 'info' };
  if (days < 14) return { label: 'Day 7–13', prompt: 'Call the borrower. Log the outcome (promise to pay / refused / no answer).', tone: 'warn' };
  if (days < 30) return { label: 'Day 14–29', prompt: 'Loan is delinquent. Keep calling. Confirm a promise-to-pay date if possible.', tone: 'warn' };
  if (days < 60) return { label: 'Day 30–59', prompt: 'Formal demand notice due. Escalate to admin if no promise-to-pay holds.', tone: 'danger' };
  if (days < 90) return { label: 'Day 60–89', prompt: 'Manual credit-bureau submission due (TransUnion ZM). Mark as reported once complete.', tone: 'danger' };
  return { label: 'Day 90+', prompt: 'Recommend for default. A super-admin must approve before write-off.', tone: 'danger' };
}

export default function EscalationBar({ daysOverdue, lastContactAt }: Props) {
  const step = nextStep(daysOverdue);
  const colour =
    step.tone === 'danger' ? 'var(--c-danger)' : step.tone === 'warn' ? 'var(--c-warning)' : 'var(--c-primary-700)';
  const style: CSSProperties = { borderLeft: `4px solid ${colour}`, paddingLeft: 16 };
  return (
    <Card>
      <div style={style}>
        <h3 style={{ margin: '0 0 6px' }}>Escalation — {step.label}</h3>
        <p style={{ margin: 0 }}>{step.prompt}</p>
        <p className="small" style={{ color: 'var(--c-text-muted)', marginTop: 6 }}>
          Last contact: <strong>{lastContactAt ? formatCatDate(lastContactAt) : 'never'}</strong>
        </p>
      </div>
    </Card>
  );
}
